import apiService from './api';
import type { ApiResponse } from '../types';
import type {
  EquipmentInsurance,
  InsurancePolicy,
  InsurancePolicyInput,
} from '../types/equipmentInsurance';
import { openDocument } from '../utils/openDocument';

/**
 * Insurance policies over the equipment register. Reading is open to the
 * whole org; the server strips money, broker and notes for anyone without a
 * vehicles creator or admin seat, and refuses their writes.
 */
class EquipmentInsuranceService {
  async list(includeEnded = false): Promise<ApiResponse<InsurancePolicy[]>> {
    return apiService.get(`/equipment/insurance/policies${includeEnded ? '?include_ended=true' : ''}`);
  }

  async get(id: number): Promise<ApiResponse<InsurancePolicy>> {
    return apiService.get(`/equipment/insurance/policies/${id}`);
  }

  async create(data: InsurancePolicyInput): Promise<ApiResponse<InsurancePolicy>> {
    return apiService.post('/equipment/insurance/policies', data);
  }

  async update(id: number, data: InsurancePolicyInput): Promise<ApiResponse<InsurancePolicy>> {
    return apiService.put(`/equipment/insurance/policies/${id}`, data);
  }

  /** A new current policy; the old one is frozen as renewed. */
  async renew(id: number, data: InsurancePolicyInput): Promise<ApiResponse<InsurancePolicy>> {
    return apiService.post(`/equipment/insurance/policies/${id}/renew`, data);
  }

  async cancel(id: number, reason: string): Promise<ApiResponse<InsurancePolicy>> {
    return apiService.post(`/equipment/insurance/policies/${id}/cancel`, { reason });
  }

  async uploadDocument(id: number, file: File): Promise<ApiResponse<InsurancePolicy>> {
    const form = new FormData();
    form.append('file', file);
    // A phone photo of the schedule over paddock signal: well past the default 10 s.
    return apiService.post(`/equipment/insurance/policies/${id}/document`, form, undefined, { timeout: 120000 });
  }

  async removeDocument(id: number): Promise<ApiResponse<InsurancePolicy>> {
    return apiService.delete(`/equipment/insurance/policies/${id}/document`);
  }

  /** The route needs the bearer token, so the file cannot be a plain link. */
  async openDocument(policy: Pick<InsurancePolicy, 'id' | 'document_name'>): Promise<void> {
    await openDocument(`/equipment/insurance/policies/${policy.id}/document`, policy.document_name || `schedule-${policy.id}`);
  }

  async forEquipment(equipmentId: number): Promise<ApiResponse<EquipmentInsurance>> {
    return apiService.get(`/equipment/insurance/equipment/${equipmentId}`);
  }

  async setNotRequired(equipmentId: number, notRequired: boolean): Promise<ApiResponse<EquipmentInsurance>> {
    return apiService.put(`/equipment/insurance/equipment/${equipmentId}/not-required`, { not_required: notRequired });
  }
}

export default new EquipmentInsuranceService();
